import type { DmxHelperPublicStatus } from "../dmxHelper/connection";
import type { PlatformLiveBridgeProvider } from "./platformLiveBridgeClient";
import type { PlatformLiveProfile } from "./platformLiveProfile";

export const DEFAULT_DMX_HELPER_URL = "http://127.0.0.1:27941";
export const ETDMX_NATIVE_WEB_URL = "http://2.0.0.100/";

export interface DmxHelperDiscovery {
  service: "platform-dmx-helper";
  apiVersion: 1;
}

export class DmxHelperBrowserRequestError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "DmxHelperBrowserRequestError";
    this.status = status;
  }
}

export interface BrowserDmxHelperClientOptions {
  baseUrl?: string;
  fetch?: typeof fetch;
  wait?: (delayMs: number) => Promise<void>;
}

const readError = async (response: Response): Promise<string> => {
  try {
    const body = (await response.json()) as { error?: unknown };
    if (typeof body.error === "string" && body.error.trim().length > 0) return body.error;
  } catch (_error) {
    // Fall through to the status-based message.
  }
  return `Platform DMX Helper returned HTTP ${response.status}.`;
};

export const withDmxHelperConnection = (profile: PlatformLiveProfile): PlatformLiveProfile => {
  const { dmxConfig: _dmxConfig, dmxConfigPath: _dmxConfigPath, ...helperProfile } = profile;
  void _dmxConfig;
  void _dmxConfigPath;
  return { ...helperProfile, dmxConnection: "helper" };
};

export const isDmxHelperRoomAssigned = (
  profile: PlatformLiveProfile,
  status: DmxHelperPublicStatus,
): boolean => {
  const assigned = status.desiredRoom;
  return (
    assigned !== null &&
    assigned.providerId === profile.bridgeProviderId &&
    assigned.profile.day === profile.day &&
    assigned.profile.platformId === profile.platformId &&
    (assigned.profile.tournamentId ?? undefined) === (profile.tournamentId ?? undefined)
  );
};

export const isDmxHelperRoomVerified = (
  profile: PlatformLiveProfile,
  status: DmxHelperPublicStatus,
): boolean =>
  status.connection === "connected" && status.roomVerified && isDmxHelperRoomAssigned(profile, status);

export const createBrowserDmxHelperClient = ({
  baseUrl = DEFAULT_DMX_HELPER_URL,
  fetch: providedFetch,
  wait = (delayMs) => new Promise((resolve) => window.setTimeout(resolve, delayMs)),
}: BrowserDmxHelperClientOptions = {}) => {
  const url = baseUrl.replace(/\/+$/, "");
  const fetchRequest: typeof fetch = providedFetch || ((input, init) => fetch(input, init));
  const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
    const response = await fetchRequest(`${url}${path}`, init);
    if (!response.ok) throw new DmxHelperBrowserRequestError(await readError(response), response.status);
    return (await response.json()) as T;
  };

  const getStatus = () => request<DmxHelperPublicStatus>("/api/integration/v1/status");

  return {
    discover: () => request<DmxHelperDiscovery>("/api/integration/v1/discovery"),
    getStatus,
    assignRoom: (
      requestedProfile: PlatformLiveProfile,
      provider: PlatformLiveBridgeProvider,
      confirmReplacement = false,
    ) =>
      request<DmxHelperPublicStatus>("/api/integration/v1/desired-room", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          assignment: {
            providerId: provider.id,
            bridgeUrl: provider.url,
            bridgeToken: provider.token,
            profile: withDmxHelperConnection(requestedProfile),
          },
          confirmReplacement,
        }),
      }),
    clearRoom: () =>
      request<DmxHelperPublicStatus>("/api/integration/v1/desired-room", { method: "DELETE" }),
    waitUntilRoomVerified: async (
      requestedProfile: PlatformLiveProfile,
      initialStatus: DmxHelperPublicStatus,
    ): Promise<DmxHelperPublicStatus> => {
      let status = initialStatus;
      for (let attempt = 0; attempt < 120; attempt += 1) {
        if (isDmxHelperRoomVerified(requestedProfile, status)) return status;
        if (status.connection === "degraded" && status.lastError !== null) throw new Error(status.lastError);
        await wait(500);
        status = await getStatus();
      }
      throw new Error("Platform DMX Helper did not verify the cloud room within 60 seconds.");
    },
  };
};
